/**
 * User Questionnaire
 *
 * In-message tap-to-reply controls for assistant questions.
 * Soft selection rows / segmented pills — no heavy bordered form chrome,
 * no scroll-gated overlay above the composer.
 */

'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import type {
  UserQuestionnaire as UserQuestionnairePayload,
  UserQuestionnaireMode,
  UserQuestionnaireOption,
  UserQuestionnaireQuestion
} from '@/types/user-questionnaire'

interface UserQuestionnaireProps {
  questionnaire: UserQuestionnairePayload
  onSend: (reply: string) => void
  /** `inline` — message-stream (default). `sheet` — soft card near composer (Spaces). */
  variant?: 'inline' | 'sheet'
  /** Hide the prompt when the bubble already shows the question text. */
  hidePrompt?: boolean
  onDismiss?: () => void
}

function getOptionReply(option: UserQuestionnaireOption) {
  return option.reply?.trim() || option.label
}

export function buildUserQuestionnaireReply(
  questionnaire: UserQuestionnaireQuestion,
  selectedOptionIds: string[]
) {
  const selectedOptions = questionnaire.options.filter((option) =>
    selectedOptionIds.includes(option.id)
  )

  if (selectedOptions.length === 0) {
    return ''
  }

  if (selectedOptions.length === 1) {
    return getOptionReply(selectedOptions[0])
  }

  const replies = selectedOptions.map(getOptionReply).filter(Boolean)
  return replies.length > 0
    ? replies.join('\n')
    : `Answer: ${selectedOptions.map((option) => option.label).join(', ')}`
}

function getQuestionnaireSteps(questionnaire: UserQuestionnairePayload) {
  return questionnaire.questions?.length ? questionnaire.questions : [questionnaire]
}

function getSkipReply(questionnaire: UserQuestionnaireQuestion) {
  return questionnaire.skipReply?.trim() || 'Skip this question.'
}

export function buildUserQuestionnaireWizardReply(
  questionnaire: UserQuestionnairePayload,
  selectedIdsByQuestionId: Record<string, string[]>
) {
  return getQuestionnaireSteps(questionnaire)
    .map((question) => {
      const reply = buildUserQuestionnaireReply(
        question,
        selectedIdsByQuestionId[question.id] ?? []
      ).trim()
      const answer = reply || (question.optional ? getSkipReply(question) : '')

      return answer ? `${question.prompt}: ${answer}` : ''
    })
    .filter(Boolean)
    .join('\n\n')
}

function modeBadgeLabel(mode: UserQuestionnaireMode, optionCount: number) {
  if (mode === 'multi') return 'Select all that apply'
  if (mode === 'yes_no') return 'Yes / No'
  if (mode === 'approval') return 'Approval'
  if (optionCount === 2) return 'Pick one'
  return 'Single choice'
}

function prefersSegmented(question: UserQuestionnaireQuestion) {
  if (question.mode === 'multi') return false
  if (question.mode === 'yes_no' || question.mode === 'approval') {
    return question.options.length <= 4
  }
  if (question.options.length < 2 || question.options.length > 5) return false
  return question.options.every((option) => {
    const label = option.label.trim()
    return label.length > 0 && label.length <= 16 && !label.includes('\n')
  })
}

function OptionControl({
  option,
  isMulti,
  isSelected,
  isDimmed,
  disabled,
  layout,
  onSelect
}: {
  option: UserQuestionnaireOption
  isMulti: boolean
  isSelected: boolean
  isDimmed: boolean
  disabled: boolean
  layout: 'row' | 'segment'
  onSelect: () => void
}) {
  if (layout === 'segment') {
    return (
      <button
        key={option.id}
        type="button"
        aria-pressed={isSelected}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          'min-h-10 flex-1 rounded-xl px-3 py-2 text-center text-sm transition-colors disabled:cursor-not-allowed',
          isSelected
            ? 'bg-primary/15 text-foreground font-medium'
            : 'text-muted-foreground hover:bg-primary/8 hover:text-foreground',
          isDimmed && 'opacity-45'
        )}
      >
        {option.label}
      </button>
    )
  }

  return (
    <button
      key={option.id}
      type="button"
      aria-pressed={isSelected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex min-h-11 w-full min-w-0 items-center gap-3 rounded-2xl px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed',
        isSelected
          ? 'bg-primary/12 text-foreground'
          : 'text-foreground/80 hover:bg-primary/6',
        isDimmed && 'opacity-45'
      )}
    >
      <span
        className={cn(
          'flex size-[18px] shrink-0 items-center justify-center border transition-colors',
          isMulti ? 'rounded-md' : 'rounded-full',
          isSelected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-foreground/25 bg-transparent'
        )}
      >
        {isSelected ? (
          isMulti ? (
            <Check className="size-3 stroke-[3]" />
          ) : (
            <span className="size-1.5 rounded-full bg-white" />
          )
        ) : null}
      </span>
      <span className="min-w-0 flex-1 break-words leading-5">{option.label}</span>
    </button>
  )
}

function QuestionBlock({
  question,
  selectedIds,
  answered,
  disabled,
  hidePrompt,
  onToggle
}: {
  question: UserQuestionnaireQuestion
  selectedIds: string[]
  answered: boolean
  disabled: boolean
  hidePrompt?: boolean
  onToggle: (option: UserQuestionnaireOption) => void
}) {
  const isMulti = question.mode === 'multi'
  const segmented = prefersSegmented(question)
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-1">
        <span className="text-primary/80 text-[11px] font-medium uppercase tracking-[0.06em]">
          {modeBadgeLabel(question.mode, question.options.length)}
        </span>
        {!hidePrompt ? (
          <p className="text-foreground text-[15px] font-semibold leading-5">
            {question.prompt}
          </p>
        ) : null}
        {question.optional ? (
          <p className="text-muted-foreground text-xs">Optional</p>
        ) : null}
      </div>

      <div
        className={cn(
          segmented
            ? 'flex flex-wrap gap-1 rounded-2xl bg-muted/40 p-1 dark:bg-white/5'
            : 'flex flex-col gap-1'
        )}
      >
        {question.options.map((option) => {
          const isSelected = selectedIdSet.has(option.id)
          return (
            <OptionControl
              key={option.id}
              option={option}
              isMulti={isMulti}
              isSelected={isSelected}
              isDimmed={answered && !isSelected}
              disabled={disabled}
              layout={segmented ? 'segment' : 'row'}
              onSelect={() => onToggle(option)}
            />
          )
        })}
      </div>
    </div>
  )
}

export function UserQuestionnaire({
  questionnaire,
  onSend,
  variant = 'inline',
  hidePrompt = variant === 'inline',
  onDismiss
}: UserQuestionnaireProps) {
  const steps = useMemo(() => getQuestionnaireSteps(questionnaire), [questionnaire])
  const isWizard = steps.length > 1
  // Multi-question: show the full stack in one message card (Memory-test style)
  // instead of a clipped one-at-a-time overlay above the composer.
  const showFlatStack = isWizard
  const [dismissed, setDismissed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)
  const [selectedIdsByQuestionId, setSelectedIdsByQuestionId] = useState<
    Record<string, string[]>
  >({})
  const [answeredId, setAnsweredId] = useState<string | null>(null)

  const singleQuestion = steps[0]
  const isSheet = variant === 'sheet'

  useEffect(() => {
    setDismissed(false)
    setIsSubmitting(false)
    isSubmittingRef.current = false
    setSelectedIdsByQuestionId({})
    setAnsweredId(null)
  }, [questionnaire.id])

  if (dismissed || !singleQuestion) {
    return null
  }

  const submitReply = (reply: string, optionId?: string) => {
    const trimmedReply = reply.trim()
    if (!trimmedReply || isSubmittingRef.current) return

    isSubmittingRef.current = true
    setIsSubmitting(true)
    if (optionId) setAnsweredId(optionId)
    onSend(trimmedReply)
  }

  const setQuestionSelection = (questionId: string, optionIds: string[]) => {
    setSelectedIdsByQuestionId((current) => ({
      ...current,
      [questionId]: optionIds
    }))
  }

  const sendWizardSelection = (
    selections: Record<string, string[]> = selectedIdsByQuestionId
  ) => {
    const reply = buildUserQuestionnaireWizardReply(questionnaire, selections).trim()
    if (reply) submitReply(reply)
  }

  const sendLegacySelection = (optionIds: string[]) => {
    const reply = buildUserQuestionnaireReply(singleQuestion, optionIds).trim()
    if (reply) submitReply(reply, optionIds[0])
  }

  const toggleFlatOption = (
    question: UserQuestionnaireQuestion,
    option: UserQuestionnaireOption
  ) => {
    if (isSubmitting) return
    const current = selectedIdsByQuestionId[question.id] ?? []
    const isMulti = question.mode === 'multi'

    if (!isMulti) {
      setQuestionSelection(question.id, [option.id])
      return
    }

    const next = current.includes(option.id)
      ? current.filter((id) => id !== option.id)
      : [...current, option.id]
    setQuestionSelection(question.id, next)
  }

  const toggleSingleOption = (option: UserQuestionnaireOption) => {
    if (isSubmitting) return
    const isMulti = singleQuestion.mode === 'multi'

    if (!isMulti) {
      setQuestionSelection(singleQuestion.id, [option.id])
      setAnsweredId(option.id)
      sendLegacySelection([option.id])
      return
    }

    const current = selectedIdsByQuestionId[singleQuestion.id] ?? []
    const next = current.includes(option.id)
      ? current.filter((id) => id !== option.id)
      : [...current, option.id]
    setQuestionSelection(singleQuestion.id, next)
  }

  const flatCanSubmit = steps.every((question) => {
    const selected = selectedIdsByQuestionId[question.id] ?? []
    return selected.length > 0 || question.optional
  })

  const singleSelected = selectedIdsByQuestionId[singleQuestion.id] ?? []
  const singleCanSubmit =
    singleSelected.length > 0 || Boolean(singleQuestion.optional)
  const showSingleSubmit =
    singleQuestion.mode === 'multi' || Boolean(singleQuestion.optional)

  const handleDismiss = () => {
    setDismissed(true)
    onDismiss?.()
  }

  return (
    <div
      className={cn(
        'relative w-full',
        isSheet
          ? 'rounded-2xl bg-muted/50 px-3 py-3 dark:bg-white/[0.04]'
          : cn(
              // Clear air from the assistant prose above and the action row below.
              // Markdown ends with last:mb-0, so the questionnaire must own the gap.
              'my-4',
              showFlatStack && 'rounded-2xl bg-muted/40 px-3 py-3 dark:bg-white/[0.04]'
            )
      )}
      role="group"
      aria-label="Answer choices"
    >
      {isSheet ? (
        <button
          type="button"
          aria-label="Close questionnaire"
          disabled={isSubmitting}
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground absolute right-2 top-2 flex size-7 items-center justify-center rounded-full"
        >
          <X className="size-4" />
        </button>
      ) : null}

      {showFlatStack ? (
        <div className={cn('flex flex-col gap-6', isSheet && 'pr-6')}>
          {steps.map((question) => (
            <QuestionBlock
              key={question.id}
              question={question}
              selectedIds={selectedIdsByQuestionId[question.id] ?? []}
              answered={false}
              disabled={isSubmitting}
              hidePrompt={false}
              onToggle={(option) => toggleFlatOption(question, option)}
            />
          ))}

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              disabled={!flatCanSubmit || isSubmitting}
              onClick={() => sendWizardSelection()}
              className={cn(
                'min-h-10 rounded-xl bg-primary/20 px-4 text-sm font-semibold text-foreground transition-opacity',
                (!flatCanSubmit || isSubmitting) && 'opacity-45'
              )}
            >
              {questionnaire.finishLabel || 'Submit answers'}
            </button>
            {steps.some((question) => question.optional) ? (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  const next = { ...selectedIdsByQuestionId }
                  for (const question of steps) {
                    if (question.optional && !(next[question.id]?.length > 0)) {
                      next[question.id] = []
                    }
                  }
                  sendWizardSelection(next)
                }}
                className="text-muted-foreground hover:text-foreground min-h-10 rounded-xl px-3 text-sm"
              >
                Skip optional
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={cn('flex flex-col gap-3', isSheet && 'pr-6')}>
          <QuestionBlock
            question={singleQuestion}
            selectedIds={singleSelected}
            answered={Boolean(answeredId)}
            disabled={isSubmitting || Boolean(answeredId)}
            hidePrompt={hidePrompt}
            onToggle={toggleSingleOption}
          />

          {showSingleSubmit && !answeredId ? (
            <div className="flex flex-wrap gap-2">
              {singleQuestion.mode === 'multi' ? (
                <button
                  type="button"
                  disabled={!singleCanSubmit || isSubmitting}
                  onClick={() => sendLegacySelection(singleSelected)}
                  className={cn(
                    'min-h-10 rounded-xl bg-primary/20 px-4 text-sm font-semibold text-foreground transition-opacity',
                    (!singleCanSubmit || isSubmitting) && 'opacity-45'
                  )}
                >
                  {singleQuestion.submitLabel || 'Submit'}
                </button>
              ) : null}
              {singleQuestion.optional ? (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => submitReply(getSkipReply(singleQuestion))}
                  className="text-muted-foreground hover:text-foreground min-h-10 rounded-xl px-3 text-sm"
                >
                  {singleQuestion.skipLabel || 'Skip'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
